/**
 * Recurring expense data access.
 *
 * Recording an occurrence invalidates the EXPENSES list as well as the
 * schedules, because it writes a real expense — that is the whole point, and a
 * ledger that did not refresh would make the money look like it had gone
 * somewhere else.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ExpenseScheduleView } from '@cenox/shared';
import { api } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';

export function useSchedules(): UseQueryResult<{ data: ExpenseScheduleView[] }> {
  const path = useBusinessPath();
  const { business } = useAuth();
  return useQuery({
    queryKey: ['expenseSchedules', business?.businessId],
    queryFn: () => api.get<{ data: ExpenseScheduleView[] }>(path('/expense-schedules')),
    enabled: Boolean(business),
    staleTime: 60_000,
  });
}

function useInvalidate(includeLedger: boolean): () => Promise<void> {
  const client = useQueryClient();
  return async () => {
    await client.invalidateQueries({ queryKey: ['expenseSchedules'] });
    if (includeLedger) {
      await client.invalidateQueries({ queryKey: ['expenses'] });
      // The dashboard reads the same expenses; a recorded occurrence is real
      // money and belongs in today's figures immediately.
      await client.invalidateQueries({ queryKey: ['dashboard'] });
    }
  };
}

export function useRecordOccurrence(): UseMutationResult<
  unknown,
  Error,
  { id: string; occurrenceOn: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate(true);
  return useMutation({
    mutationFn: ({ id, occurrenceOn }) =>
      api.post(path(`/expense-schedules/${id}/occurrences`), { occurrenceOn }),
    onSuccess: invalidate,
  });
}

export function useSkipOccurrence(): UseMutationResult<
  unknown,
  Error,
  { id: string; reason: string }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate(false);
  return useMutation({
    mutationFn: ({ id, reason }) => api.post(path(`/expense-schedules/${id}/skip`), { reason }),
    onSuccess: invalidate,
  });
}

/**
 * Archive a stopped schedule.
 *
 * Removes nothing: the row and every occurrence it produced survive, and the
 * list simply stops showing it. The API refuses anything that is not stopped.
 */
export function useDeleteSchedule(): UseMutationResult<unknown, Error, string> {
  const path = useBusinessPath();
  const invalidate = useInvalidate(false);
  return useMutation({
    mutationFn: (id) => api.del(path(`/expense-schedules/${id}`)),
    onSuccess: invalidate,
  });
}

export function useSetScheduleStatus(): UseMutationResult<
  unknown,
  Error,
  { id: string; status: 'active' | 'paused' | 'stopped' }
> {
  const path = useBusinessPath();
  const invalidate = useInvalidate(false);
  return useMutation({
    mutationFn: ({ id, status }) => api.post(path(`/expense-schedules/${id}/status`), { status }),
    onSuccess: invalidate,
  });
}
